import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageCTA } from '../components/PageCTA';
import { ChevronDown, Target, Zap, ShieldCheck } from 'lucide-react';
export function FAQPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const faqs = [{
    question: '¿Esto es solo diseño o incluye estrategia?',
    answer: 'Incluye ambas. Un diseño bonito sin estrategia no vende. Analizamos tu negocio, definimos la mejor estructura para convertir visitas en clientes y luego aplicamos un diseño premium que transmita confianza. Cada botón, texto y sección tiene un propósito específico en el embudo de conversión.'
  }, {
    question: '¿Cuánto tiempo tarda el proceso completo?',
    answer: 'El tiempo medio de desarrollo es de 2 a 4 semanas, dependiendo de la complejidad del proyecto y la rapidez con la que nos proporciones el material necesario (textos, imágenes, logo). Proyectos más sencillos o urgentes pueden estar listos en 10 días laborables si hay buena coordinación.'
  }, {
    question: '¿Qué necesito proporcionar para empezar?',
    answer: 'Básicamente, información sobre tu negocio: qué haces, a quién te diriges, qué te diferencia de la competencia. Si tienes logo, fotos profesionales y textos, perfecto. Si no los tienes, no te preocupes; podemos orientarte, usar recursos de stock premium o incluir estos servicios como parte del proyecto.'
  }, {
    question: '¿Podré actualizar mi web yo mismo?',
    answer: 'Sí, absolutamente. Entregamos el sistema con un panel autogestionable muy intuitivo. Podrás cambiar textos, subir nuevas imágenes, publicar artículos o actualizar tu menú/catálogo sin depender de nosotros, sin pagar cuotas extra y sin necesidad de saber absolutamente nada de código.'
  }, {
    question: '¿Está optimizado para móvil?',
    answer: "El 80% de tus clientes te buscarán desde su teléfono. Por eso diseñamos primero para móvil (Mobile First), asegurando que la experiencia sea rápida, fluida y perfecta en cualquier pantalla. No es solo que 'se vea bien', es que sea fácil navegar y comprar desde un smartphone."
  }, {
    question: '¿Incluye la configuración de redes sociales?',
    answer: 'Sí, es una parte fundamental de nuestro ecosistema. Configuramos tus perfiles profesionales, creamos una imagen de marca coherente con tu nueva web y dejamos todo conectado para que el tráfico fluya de forma natural entre tus redes sociales y tu página principal.'
  }, {
    question: '¿Ofrecéis mantenimiento posterior?',
    answer: 'Sí. Una vez entregado el proyecto, la web es 100% tuya. Sin embargo, ofrecemos planes opcionales de soporte técnico y mantenimiento para que tu presencia digital siga funcionando perfectamente, esté siempre actualizada y segura, y puedas pedirnos ajustes cuando tu negocio evolucione.'
  }, {
    question: '¿Puedo contratar solo uno de los servicios?',
    answer: 'Nuestro enfoque es integral porque creemos firmemente que los mejores resultados se consiguen cuando todo está conectado en un ecosistema. Sin embargo, entendemos que cada negocio está en una fase diferente. Podemos adaptar el paquete a tus necesidades específicas tras una primera conversación de diagnóstico.'
  }, {
    question: '¿Para qué tipo de negocio sirve?',
    answer: 'Nuestro sistema es ideal para negocios locales (restaurantes, clínicas, peluquerías, tiendas), empresas de servicios B2B/B2C y marcas personales que necesitan profesionalizar su imagen, destacar frente a su competencia local y captar clientes online de forma recurrente y predecible.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="pt-24" data-id="element-513">
      {/* Page Header */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" data-id="element-514">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" data-id="element-515"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-brand-emerald/15 blur-[120px] rounded-full pointer-events-none" data-id="element-516"></div>

        {/* Decorative Network SVG */}
        <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-517">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-518">
            <motion.g animate={{
            y: [-10, 10, -10],
            opacity: [0.3, 0.7, 0.3]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-519">
              <circle cx="15%" cy="30%" r="3" fill="#10b981" data-id="element-520" />
              <circle cx="25%" cy="20%" r="2" fill="#10b981" data-id="element-521" />
              <line x1="15%" y1="30%" x2="25%" y2="20%" stroke="#10b981" strokeWidth="1" opacity="0.4" data-id="element-522" />
            </motion.g>
            <motion.g animate={{
            y: [10, -10, 10],
            opacity: [0.3, 0.7, 0.3]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} data-id="element-523">
              <circle cx="85%" cy="60%" r="4" fill="#10b981" data-id="element-524" />
              <circle cx="75%" cy="70%" r="2" fill="#10b981" data-id="element-525" />
              <line x1="85%" y1="60%" x2="75%" y2="70%" stroke="#10b981" strokeWidth="1" opacity="0.4" data-id="element-526" />
            </motion.g>
          </svg>
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-527">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-528">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter" data-id="element-529">
              Preguntas <span className="text-gradient" data-id="element-530">Frecuentes</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 leading-relaxed font-medium" data-id="element-531">
              Resolvemos todas tus dudas sobre nuestro proceso, metodología y
              resultados antes de empezar a trabajar juntos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-[#050505] relative" data-id="element-532">
        <div className="section-divider absolute top-0" data-id="element-533"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-534"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl relative z-10" data-id="element-535">
          <div className="space-y-4" data-id="element-536">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className={`bg-gradient-to-br rounded-2xl border transition-all duration-500 overflow-hidden ${isOpen ? 'from-[#151515] to-[#0a1a12] border-brand-emerald/30 shadow-[0_0_40px_rgba(16,185,129,0.1)]' : 'from-[#111] to-[#0a0a0a] border-white/5 hover:border-white/10 hover:shadow-lg'}`} data-id="element-537">
                  <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-6 md:px-8 py-6 text-left flex justify-between items-center focus:outline-none group relative" data-id="element-538">
                    {isOpen && <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-emerald" data-id="element-539"></div>}
                    <div className="flex items-center gap-4 md:gap-6 pr-8" data-id="element-540">
                      <span className={`text-sm font-black tracking-widest ${isOpen ? 'text-brand-emerald' : 'text-white/20 group-hover:text-white/40'} transition-colors`} data-id="element-541">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className={`font-bold transition-colors text-lg md:text-xl tracking-tight ${isOpen ? 'text-brand-emerald' : 'text-gray-200 group-hover:text-white'}`} data-id="element-542">
                        {faq.question}
                      </span>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-500 ${isOpen ? 'bg-brand-emerald/10 text-brand-emerald rotate-180' : 'bg-white/5 text-gray-500 group-hover:bg-white/10 group-hover:text-gray-300'}`} data-id="element-543">
                      <ChevronDown size={20} data-id="element-544" />
                    </div>
                  </button>
                  <AnimatePresence data-id="element-545">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} data-id="element-546">
                        <div className="px-6 md:px-8 pb-8 text-gray-400 border-t border-white/5 pt-6 leading-relaxed text-lg font-medium" data-id="element-547">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* About Growthly */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" data-id="element-548">
        <div className="section-divider absolute top-0" data-id="element-549"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-550"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[400px] bg-brand-emerald/10 blur-[150px] rounded-full pointer-events-none" data-id="element-551"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-6xl relative z-10" data-id="element-552">
          <div className="text-center mb-14" data-id="element-553">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-554">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tighter" data-id="element-555">
                Sobre <span className="text-gradient" data-id="element-556">Growthly</span>
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-557">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8 text-gray-400 text-lg md:text-xl leading-relaxed font-medium" data-id="element-558">
              <p data-id="element-559">
                En Growthly creemos que todo negocio, sin importar su tamaño,
                merece una presencia digital que refleje su verdadero valor.
                Vemos a diario excelentes profesionales y negocios locales
                perder clientes simplemente porque su imagen en internet no
                transmite la calidad real de su trabajo.
              </p>
              <p data-id="element-560">
                Nacimos con el objetivo de democratizar el acceso a ecosistemas
                digitales premium. No somos una agencia tradicional que te vende
                una web y desaparece. Somos tu socio tecnológico estratégico.
              </p>
              <div className="p-8 bg-gradient-to-br from-brand-emerald/10 to-transparent border-l-4 border-brand-emerald rounded-r-2xl" data-id="element-561">
                <p className="text-white font-bold text-xl md:text-2xl leading-tight" data-id="element-562">
                  "Nuestra visión es clara: transformar negocios tradicionales
                  en máquinas digitales eficientes, preparadas para competir y
                  ganar en la economía actual."
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-563">
              {[{
              icon: <Target size={28} data-id="element-564" />,
              title: 'Simplicidad',
              desc: 'Eliminamos la jerga técnica. Hacemos que el proceso de digitalización sea fácil, transparente y sin estrés para ti.'
            }, {
              icon: <Zap size={28} data-id="element-565" />,
              title: 'Eficiencia',
              desc: 'No reinventamos la rueda. Usamos metodologías probadas que garantizan tiempos de entrega rápidos y resultados sólidos.'
            }, {
              icon: <ShieldCheck size={28} data-id="element-566" />,
              title: 'Resultados',
              desc: 'Todo lo que construimos tiene un único objetivo final: hacer que tu negocio sea más rentable y visible.'
            }].map((pillar, i) => <div key={i} className="card-premium p-8 flex gap-6 group relative overflow-hidden" data-id="element-567">
                  <div className="absolute -top-6 -right-2 text-8xl font-black text-white/[0.02] select-none pointer-events-none group-hover:text-brand-emerald/[0.04] transition-colors duration-500" data-id="element-568">
                    0{i + 1}
                  </div>
                  <div className="icon-premium shrink-0 group-hover:scale-110 group-hover:shadow-[inset_0_0_30px_rgba(16,185,129,0.2)] transition-all duration-500 relative z-10" data-id="element-569">
                    {pillar.icon}
                  </div>
                  <div className="relative z-10" data-id="element-570">
                    <h3 className="text-2xl font-bold text-white mb-2 tracking-tight" data-id="element-571">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed font-medium" data-id="element-572">
                      {pillar.desc}
                    </p>
                  </div>
                </div>)}
            </motion.div>
          </div>
        </div>
      </section>

      <PageCTA data-id="element-573" />
    </div>;
}